import { forwardRef, type ButtonHTMLAttributes, type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Icon, type IconName } from "./Icon";
import { cx } from "../lib/cx";
import { useI18n } from "../i18n";

export type ButtonVariant = "primary" | "secondary" | "default" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconLeft?: IconName;
  iconRight?: IconName;
  /** ปุ่มไอคอนล้วน ต้องส่ง aria-label */
  iconOnly?: boolean;
  loading?: boolean;
  /** ฟีเจอร์ที่ต้องอัปเกรด: แสดงป้ายกุญแจ กดแล้วเรียก onClick (เปิด Paywall) ไม่ใช่ disabled */
  locked?: { plan: "standard" };
  block?: boolean;
  /** ลิงก์ภายในแอป (render เป็น <a> ผ่าน React Router) */
  to?: string;
  /** state ของ React Router สำหรับ to */
  state?: unknown;
  /** ลิงก์ภายนอก */
  href?: string;
  children?: ReactNode;
}

type ClassProps = Pick<ButtonProps, "variant" | "size" | "iconOnly" | "loading" | "block" | "className">;

function classes({ variant = "default", size = "md", iconOnly, loading, block, className }: ClassProps): string {
  return cx(
    "btn",
    variant !== "default" && `btn-${variant}`,
    size === "lg" && "btn-lg",
    size === "sm" && "btn-sm",
    iconOnly && "btn-icon",
    block && "btn-block",
    loading && "is-loading",
    className,
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, ref) {
  const { variant, size, iconLeft, iconRight, iconOnly, loading, locked, block, to, state, href, children, className, disabled, onClick, type, ...rest } = props;
  const { t } = useI18n();
  const cls = classes({ variant, size, iconOnly, loading, block, className });
  const content = (
    <>
      {iconLeft && <Icon name={iconLeft} />}
      {children}
      {iconRight && <Icon name={iconRight} />}
      {locked && (
        <span className="lock">
          <Icon name="lock" size="sm" />
          {t("common.upgrade")}
        </span>
      )}
    </>
  );

  if (to && !disabled) {
    return (
      <Link to={to} state={state} className={cls} aria-label={rest["aria-label"]} title={rest.title} style={rest.style as CSSProperties | undefined}>
        {content}
      </Link>
    );
  }
  if (href && !disabled) {
    return (
      <a href={href} className={cls} aria-label={rest["aria-label"]} title={rest.title} style={rest.style as CSSProperties | undefined}>
        {content}
      </a>
    );
  }

  const busy = Boolean(loading);
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      className={cls}
      disabled={disabled}
      aria-busy={busy || undefined}
      onClick={busy ? (e) => e.preventDefault() : onClick}
      {...rest}
    >
      {content}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonProps, "iconOnly" | "children" | "iconLeft" | "iconRight"> {
  icon: IconName;
  "aria-label": string;
}

/** ปุ่มไอคอน 44px (size lg = 56px ในแถบล่าง) ต้องมี aria-label และ title */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton({ icon, title, ...rest }, ref) {
  return <Button ref={ref} iconOnly iconLeft={icon} title={title ?? rest["aria-label"]} {...rest} />;
});
